import { Component, ElementRef, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { HeaderService } from './header.service';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '@callcenter-env/environment';
import { HxAuthService, HxCallcenterService, HxError, HxFioPipe, HxUserService, UserBasicModel } from 'hx-services';
import { HxLanguageSelectComponent, HxToastrService } from 'hx-component';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { HcNotifierComponent } from '../hc-notifier/hc-notifier.component';
import { TranslocoPipe } from '@jsverse/transloco';
import Keycloak from 'keycloak-js';
import { OrderDraftStore } from '@callcenter-app/pages/order-editor/state/order-draft.store';
import { CallStateService } from '@callcenter-app/service/call-state.service';
import { CallLinkService } from '@callcenter-app/service/call-link.service';
import { isOldWorkplaceUrl, WORKPLACE_READY } from '@callcenter-app/service/workplace.config';
import { filter, Observable, Subscription } from 'rxjs';

/**
 * Component of site header
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  imports: [NgClass, RouterLink, NgIf, HcNotifierComponent, NgFor, TranslocoPipe, HxLanguageSelectComponent, HxFioPipe]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('templateRef', {static: true}) templateRef!: TemplateRef<any>;
  public isOnline = window.navigator.onLine ?? true;
  operatorIdList: number[] = [];
  userList: UserBasicModel[] = [];
  fullname!: string;
  operator!: string;
  open = false;
  submenu = false;
  isProd: boolean = environment.production;
  isLoading = {
    operator: 'default',
    userList: false,
    id: true
  };
  position = '';
  showDropdown = false;

  private subs = new Subscription();

  @HostListener('document:click', ['$event']) handleKeyboardEvent(event: Event) {
    if (this.submenu) {
      if (!this._eref.nativeElement.contains(event.target)) {
        this.toggleSubmenu();
      }
    }
  }

  constructor(
    private _eref: ElementRef,
    private headerService: HeaderService,
    private toastr: HxToastrService,
    private keycloak: Keycloak,
    private userService: HxUserService,
    private router: Router,
    private callcenterService: HxCallcenterService,
    private auth: HxAuthService,
    public modal: NgbModal,
    private drafts?: OrderDraftStore,
    private callState?: CallStateService,
    private callLink?: CallLinkService,
  ) {
    const ls = localStorage.getItem('sidebar');
    this.open = ls === 'minimize';
    if (this.open) {
      this.toggleSidebar();
    }
  }

  ngOnInit() {
    this.loadOperatorIds();
    this.checkNetwork();
    const user = this.auth.user;
    this.operator = user.asteriskId ?? '';

    this.fullname = user.shortname ?? '';
    this.position = user.position ?? '';

    if (this.callState && WORKPLACE_READY) {
      // the number in the header follows the number picked anywhere (the band, the workplace) — no reload any more
      this.subs.add(this.callState.state$.subscribe(state => this.operator = state.slot ?? ''));
      // the window for the number opens by itself on the old page only (stage 4): elsewhere the band and the workplace offer the numbers
      this.subs.add(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => this.openWindowOnOldPage(e.urlAfterRedirects)));
      this.openWindowOnOldPage(this.router.url);
    } else if (!this.operator) {
      this.changeOperatorId();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  isCurrentOperatorId(operatorId: number): boolean {
    return operatorId === +this.operator || this.isOperatorBuzy(operatorId);
  }

  showBusyOperator(operatorId: number): string {
    return this.userList.find(item => item.asteriskId === `${operatorId}`)?.fullname ?? '';
  }

  changeOperatorId() {
    this.submenu = false;
    this.loadBusyOperators();
    this.modal.open(this.templateRef, {size: 'sm'});
  }

  setOperatorId(operatorId: number) {
    if (!operatorId) {
      return;
    }

    this.isLoading.operator = 'loading';

    // the old page learns of the number only at its start (it reads it once): there the page is reloaded as it always was; anywhere else the shell takes the number at once
    const shell = this.callState && WORKPLACE_READY && !isOldWorkplaceUrl(this.router.url) ? this.callState : undefined;
    const done: Observable<unknown> = shell ? shell.changeSlot(operatorId) : this.userService.setOperatorId(operatorId);
    done.subscribe(() => {
      if (shell) {
        this.isLoading.operator = 'default';
        this.modal.dismissAll();
      } else {
        window.location.reload();
        this.isLoading.operator = 'reload';
      }
    }, (err: HttpErrorResponse) => {
      // the number is taken or not allowed for the operator's groups — back to the list, which may have changed
      const ex = err.error as Partial<HxError> | null;
      this.isLoading.operator = 'default';
      this.toastr.error(ex?.message ?? 'header.operatorModal.setError', ex?.data);
      this.loadBusyOperators();
    });
  }

  toggleSidebar(): void {
    const body = document.getElementsByTagName('body')[0];
    const hasClass = body.className.indexOf('--minimize');

    if (hasClass !== -1 && !this.open) {
      body.classList.remove('m-aside-left--minimize');
      body.classList.remove('m-brand--minimize');
      this.setLs('');
    } else {
      body.classList.add('m-aside-left--minimize');
      body.classList.add('m-brand--minimize');
      this.setLs('minimize');
    }

    this.open = !this.open;
  }

  toggleMobSidebar(): void {
    const body = document.getElementsByTagName('body')[0];
    const hasClass = body.className.indexOf('--on');

    if (hasClass !== -1 && this.open) {
      body.classList.remove('m-aside-left--on');
    } else {
      body.classList.add('m-aside-left--on');
    }

    this.open = !this.open;
  }

  toggleSubmenu(): void {
    this.submenu = !this.submenu;
  }

  logout() {
    // the drafts of «Новый заказ» in this tab hold clients' numbers: they do not outlive the session (security 2a1-P3-11)
    this.drafts?.purgeAll();
    this.callState?.purge();
    this.callLink?.purge();
    this.keycloak.logout();
  }

  navigateUrl(url: string) {
    this.toggleSubmenu();
    this.router.navigateByUrl(`/${url}`);
  }

  /** The window of the number opens by itself when the old page is entered without a number. */
  private openWindowOnOldPage(url: string): void {
    if (isOldWorkplaceUrl(url) && !this.operator) {
      this.changeOperatorId();
    }
  }

  private checkNetwork() {
    this.headerService.network().subscribe(result => {
      this.isOnline = result === 'online';
      const msg = `network.${result}`;
      if (result === 'online') {
        this.toastr.success(msg);
      } else {
        this.toastr.warning(msg);
      }
    });
  }

  private setLs(str: string): void {
    localStorage.setItem('sidebar', str);
  }

  private loadBusyOperators() {
    this.isLoading.userList = true;
    this.userService.getOperatorIdList().subscribe(result => {
      this.userList = result;
      this.isLoading.userList = false;
    }, () => this.isLoading.userList = false);
  }

  private isOperatorBuzy(operatorId: number): boolean {
    return this.userList.some(item => item.asteriskId === `${operatorId}`);
  }

  private async loadOperatorIds() {
    this.isLoading.id = true;
    try {
      this.operatorIdList = await this.callcenterService.getOperatorIds();
    } finally {
      this.isLoading.id = false;
    }
  }
}
